import React, { useState, useEffect, useCallback, useContext, memo, useRef, useMemo } from 'react';
import DatePicker from "react-datepicker";
import { AuthContext } from '../context/authContext';
import { useFormContext } from "../context/FormContext";

import { Input, Label, Button, Select_simple } from "./ui";
import { optionsObjetoExpediente, optionsObjetoExpediente_Proyectos, optionsObjetoExpediente_Mejoras_Microcreditos } from "../datos/options";

//importar constantes para Area Responsable
import { AREA_RESP_OBRAS, AREA_RESP_MEJORAS, AREA_RESP_MICROCREDITOS, AREA_RESP_INTERVENCION_SOCIAL, AREA_RESP_OTRA, ESTADO_ANALISIS_PRELIMINAR, ESTADO_CONFECCION_EXPEDIENTE } from "../constants/config";

// Formatear parte entera con separador de miles (puntos)
const formatInteger = (str) => {
    return str.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

// Formatear monto para display: parte entera con puntos, coma decimal
const formatMontoDisplay = (raw) => {
    if (!raw) return '';
    // Normalizar: si el backend guardó con punto decimal (ej. "2005.5"),
    // convertirlo a coma para que split funcione correctamente
    const normalizado = raw.replace(/\.(\d+)$/, ',$1');
    const parts = normalizado.split(',');
    const intPart = parts[0].replace(/\D/g, '');
    const decPart = parts.length > 1 ? ',' + parts.slice(1).join('') : '';
    return formatInteger(intPart) + decPart;
};

// Limpiar input del usuario: solo dígitos y una coma, sin puntos
const cleanMontoInput = (str) => {
    const noDots = str.replace(/\./g, '');
    const digitsAndComma = noDots.replace(/[^\d,]/g, '');
    const parts = digitsAndComma.split(',');
    if (parts.length > 2) return parts[0] + ',' + parts.slice(1).join('');
    return digitsAndComma;
};

// Calcular posición del cursor en el string formateado
const computeCursorPos = (raw, rawCursor) => {
    const beforeCursor = raw.substring(0, rawCursor);
    const intPart = beforeCursor.split(',')[0];
    return formatInteger(intPart).length + (intPart.length < beforeCursor.length ? beforeCursor.length - intPart.length : 0);
};

// Limpiar separadores para almacenar (formato backend: 1.500,50 → 1500.50)
const parseMonto = (str) => {
    if (!str) return '';
    const limpio = str.replace(/\./g, '').replace(',', '.');
    // No mandar al backend un valor incompleto (",", "1500,", "."): NUMERIC lo
    // guardaria raro o fallaria. Se completa cuando el usuario sigue escribiendo.
    if (limpio === '.' || limpio === '-' || limpio === '-.') return '';
    return limpio.endsWith('.') ? limpio.slice(0, -1) : limpio;
};

// Opciones de repartición para el select
const optionsReparticion = [
    { value: '', label: 'Seleccionar...' },
    { value: 'DPPHMDCGP', label: 'DPPHMDCGP' },
    { value: 'DPICMDCGP', label: 'DPICMDCGP' },
    { value: 'SSTYAHMDSGP', label: 'SSTYAHMDSGP' },
    { value: 'DPAJHYDBMDSGP', label: 'DPAJHYDBMDSGP' }
];

// Componentes memoizados para evitar re-renders innecesarios
const MemoizedInput = memo(Input);
const MemoizedSelect = memo(Select_simple);

const ExpedienteSubform = ({ disabled = false }) => {
    const { user, token } = useContext(AuthContext);
    const { formA, formC, updateField, loadField, markSectionDirty } = useFormContext();
    const { datosGenerales = {} } = formA;
    const { estado } = datosGenerales;

    const descomponerExpediente = useCallback((expediente) => {
        if (!expediente) return null;
        
        let cleanExp = expediente.trim().replace(/-\s+-/g, '--').replace(/\s+/g, '');
        
        const formatoCorrecto = /^EX-(\d{4})-(\d{8})-GDEBA-([A-Z]{8,13})$/.exec(cleanExp);
        if (formatoCorrecto) {
            return {
                esValido: true,
                exp_anio: formatoCorrecto[1],
                exp_numero: formatoCorrecto[2],
                exp_reparticion: formatoCorrecto[3],
                esLegacy: false
            };
        }
        
        const formatoViejo = /^(\d{4})-(\d{8})-([A-Z]{8,13})$/.exec(cleanExp);
        if (formatoViejo) {
            return {
                esValido: true,
                exp_anio: formatoViejo[1],
                exp_numero: formatoViejo[2],
                exp_reparticion: formatoViejo[3],
                esLegacy: true
            };
        }
        
        const matchDobleGuion = cleanExp.match(/^EX-(\d{4})-(\d{1,8})--?GDEBA-([A-Z]{8,13})$/);
        if (matchDobleGuion) {
            return {
                exp_anio: matchDobleGuion[1],
                exp_numero: matchDobleGuion[2].padStart(8, '0'),
                exp_reparticion: matchDobleGuion[3],
                esValido: true,
                fueNormalizado: true
            };
        }
        
        const matchSinGDEBA = cleanExp.match(/^EX-(\d{4})-(\d{1,8})-([A-Z]{8,13})$/);
        if (matchSinGDEBA) {
            return {
                exp_anio: matchSinGDEBA[1],
                exp_numero: matchSinGDEBA[2].padStart(8, '0'),
                exp_reparticion: matchSinGDEBA[3],
                esValido: true,
                fueNormalizado: true
            };
        }
        
        const matchCompacto = cleanExp.match(/^EX(\d{4})-(\d{1,8})-([A-Z]{8,13})$/);
        if (matchCompacto) {
            return {
                exp_anio: matchCompacto[1],
                exp_numero: matchCompacto[2].padStart(8, '0'),
                exp_reparticion: matchCompacto[3],
                esValido: true,
                fueNormalizado: true
            };
        }
        
        const matchFlexible = cleanExp.match(/EX-?(\d{4})-?(\d{1,8})-?-?(?:GD?E?B?A?)?-?([A-Z]{8,13})/i);
        if (matchFlexible) {
            return {
                exp_anio: matchFlexible[1],
                exp_numero: matchFlexible[2].padStart(8, '0'),
                exp_reparticion: matchFlexible[3].toUpperCase(),
                esValido: true,
                fueNormalizado: true
            };
        }
        
        const matchLegacy = cleanExp.match(/^2423-(\d{4})\/(\d{2})$/);
        if (matchLegacy) {
            return {
                esValido: true,
                esLegacy: true
            };
        }

        return { esValido: false, valorOriginal: expediente };
    }, []);

    // Estado local para manejar los expedientes
    const [expedientes, setExpedientes] = useState(() => {
        const initialData = formA?.array_obras_expediente;
        if (Array.isArray(initialData) && initialData.length > 0) {
            return initialData.map(exp => {
                if (exp && exp.expediente_o && exp.formatoCorrecto === undefined) {
                    const descompuesto = descomponerExpediente(exp.expediente_o);
                    const esLegacyBackend = exp.es_legacy === true;
                    return {
                        ...exp,
                        formatoCorrecto: descompuesto?.esValido === true,
                        valorOriginal: exp.expediente_o,
                        ...(descompuesto?.esValido ? {
                            exp_anio: descompuesto.exp_anio,
                            exp_numero: descompuesto.exp_numero,
                            exp_reparticion: descompuesto.exp_reparticion,
                            esLegacy: descompuesto.esLegacy === true,
                        } : {}),
                        // Si el backend marca el formato como legacy, priorizarlo
                        es_legacy: esLegacyBackend ? true : (descompuesto?.esLegacy === true)
                    };
                }
                return exp;
            });
        }
        return [{
            expediente_o: "",
            objeto_o: estado === 'En circuito administrativo' ? "Proyecto" : "",
            fecha_caratulacion_o: null,
            descripcion_objeto_o: ""
        }];
    });

    const tieneExpProyectoValido = useMemo(() => {
        return expedientes.some(e => {
            if (!e) return false;
            if (e.objeto_o !== 'Proyecto') return false;
            if (e.formatoCorrecto === true) return true;
            const anio = e.exp_anio || '';
            const numero = e.exp_numero || '';
            const reparticion = e.exp_reparticion || '';
            return anio.length === 4 && numero.length > 0 && reparticion.length > 0;
        });
    }, [expedientes]);

    const tieneExpProyecto = useMemo(() => {
        return expedientes.some(e => e && e.objeto_o === 'Proyecto');
    }, [expedientes]);

    const tieneExpRendicionValido = useMemo(() => {
        return expedientes.some(e => {
            if (!e) return false;
            if (e.objeto_o !== 'Rendición') return false;
            if (e.formatoCorrecto === true) return true;
            const anio = e.exp_anio || '';
            const numero = e.exp_numero || '';
            const reparticion = e.exp_reparticion || '';
            return anio.length === 4 && numero.length > 0 && reparticion.length > 0;
        });
    }, [expedientes]);

    // Estado para almacenar si el expediente existe en la BD
    // Ref para valores crudos de monto_adenda (solo dígitos + coma opcional, sin puntos).
    // Es solo un buffer de tipeo mientras la coma decimal esta incompleta
    // (p. ej. "1500,"): se indexa por posicion, asi que hay que limpiarlo cuando
    // cambia la lista de expedientes (otra obra, alta o baja) o mostraria el monto
    // de la fila anterior.
    const montoAdendaRawRef = useRef({});

    const [verificacion, setVerificacion] = useState({});
    const [buttonIsDisabledState, setButtonIsDisabledState] = useState(false);

    // Refs
    const initialProcessedRef = useRef(false);
    const isEditingLocally = useRef(false);
    const expedientesRef = useRef(expedientes);
    const isUpdatingFromEffect = useRef(false);
    const ultimoValorVerificadoRef = useRef(null);
    const expedientesGuardadosRef = useRef(new Set());
    const datosCargadosDelContexto = useRef(false);
    const hasPendingEditRef = useRef(false);
    // De qué obra viene el estado local `expedientes`. La longitud NO
    // alcanza para saber si hay que recargar: al entrar de una obra a otra con la misma
    // cantidad de expedientes (p. ej. 3 y 3) no hay nada que detecte el cambio y el
    // form queda mostrando los expedientes de la obra anterior. Solo se usa como
    // guarda del cambio de obra, nunca para decidir si se pisa lo que edita el usuario.
    const obraIdDeExpedientesRef = useRef(datosGenerales?.id_partido_obra || null);

    expedientesRef.current = expedientes;

    // Al cambiar de obra, el buffer de tipeo del monto de la adenda queda viejo:
    // se descarta para que el valor se lea de los expedientes de la obra nueva.
    useEffect(() => {
        montoAdendaRawRef.current = {};
    }, [datosGenerales?.id_partido_obra]);

    const normalizarExpediente = useCallback((expediente) => {
        if (!expediente) return '';
        
        let cleanExp = expediente.trim().replace(/-\s+-/g, '--').replace(/\s+/g, '');
        
        if (/^EX-\d{4}-\d{8}-GDEBA-[A-Z]{8,13}$/.test(cleanExp)) {
            return cleanExp;
        }
        
        const matchSinGuion = cleanExp.match(/^EX(\d{4})-(\d{8})-([A-Z]+)$/);
        if (matchSinGuion) {
            const year = matchSinGuion[1];
            const numero = matchSinGuion[2].padStart(8, '0');
            const reparticion = matchSinGuion[3];
            return `EX-${year}-${numero}-GDEBA-${reparticion}`;
        }
        
        const matchDobleGuion = cleanExp.match(/^EX(\d{4})-(\d{8})--GED?EBA-([A-Z]+)$/);
        if (matchDobleGuion) {
            const year = matchDobleGuion[1];
            const numero = matchDobleGuion[2].padStart(8, '0');
            const reparticion = matchDobleGuion[3];
            return `EX-${year}-${numero}-GDEBA-${reparticion}`;
        }
        
        const matchSinGDEBA = cleanExp.match(/^EX-(\d{4})-(\d{8})-([A-Z]{8,13})$/);
        if (matchSinGDEBA) {
            const year = matchSinGDEBA[1];
            const numero = matchSinGDEBA[2].padStart(8, '0');
            const reparticion = matchSinGDEBA[3];
            return `EX-${year}-${numero}-GDEBA-${reparticion}`;
        }
        
        // Formato legacy: devolver tal cual
        if (/^2423-\d{4}\/\d{2}$/.test(cleanExp)) {
            return cleanExp;
        }
        
        return null;
    }, []);

    const esFormatoValido = useCallback((expediente) => {
        if (!expediente) return false;
        const limpio = expediente.trim();
        if (/^EX-\d{4}-\d{8}-GDEBA-[A-Z]{8,13}$/.test(limpio)) return true;
        if (/^2423-\d{4}\/\d{2}$/.test(limpio)) return true;
        return false;
    }, []);

    const aplicarMascaraExpediente = useCallback((valor) => {
        if (!valor) return '';
        
        const limpio = valor.toUpperCase().replace(/[^A-Z0-9]/g, '');
        let resultado = '';
        
        if (limpio.length >= 0) {
            resultado = 'EX';
        }
        
        if (limpio.length >= 3) {
            const anio = limpio.substring(2, 6);
            resultado += '-' + anio.padEnd(4, 'Y');
        }
        
        if (limpio.length >= 7) {
            const numero = limpio.substring(6, 14);
            resultado += '-' + numero.padEnd(8, 'X');
        }
        
        if (limpio.length >= 15) {
            resultado += '-GDEBA';
        }
        
        if (limpio.length >= 16) {
            const reparticion = limpio.substring(15, 23);
            resultado += '-' + reparticion.padEnd(8, 'X');
        }
        
        return resultado;
    }, []);

    const getOptions = useCallback(() => {
        let base;
        if (datosGenerales.area_responsable === AREA_RESP_OBRAS) {
            base = optionsObjetoExpediente_Proyectos;
        } else if (datosGenerales.area_responsable === AREA_RESP_MEJORAS) {
            base = optionsObjetoExpediente_Mejoras_Microcreditos;
        } else if (datosGenerales.area_responsable === AREA_RESP_MICROCREDITOS) {
            base = [
                { value: "", label: "Seleccionar..." },
                { value: "Proyecto", label: "Proyecto" },
                { value: "Rendición", label: "Rendición" }
            ];
        } else {
            base = optionsObjetoExpediente;
        }
        if (estado === 'Rescisión') {
            return [...base, { value: "Rescisión", label: "Rescisión" }];
        }
        return base;
    }, [datosGenerales.area_responsable, estado]);

    // Limites por tipo de objeto
    const LIMITES_OBJETO = { Proyecto: 1, Rendición: 1, Adenda: 2, Rescisión: 1 };

    // Para un indice dado, retorna las opciones con disabled segun los limites
    const getOptionsConLimites = useCallback((index) => {
        const opcionesBase = getOptions();
        const conteo = {};
        expedientes.forEach((exp, i) => {
            if (i === index) return;
            const obj = exp?.objeto_o;
            if (obj) conteo[obj] = (conteo[obj] || 0) + 1;
        });
        return opcionesBase.map(opt => {
            if (!opt.value) return opt;
            const limite = LIMITES_OBJETO[opt.value];
            const alcanzado = limite && (conteo[opt.value] || 0) >= limite;
            const esSeleccionActual = opt.value === expedientes[index]?.objeto_o;

            const sinProyecto = opt.value === 'Rendición' && !tieneExpProyectoValido;
            const sinRendicion = opt.value === 'Adenda' && !tieneExpRendicionValido;

            return { ...opt, disabled: (alcanzado || sinProyecto || sinRendicion) && !esSeleccionActual };
        });
    }, [expedientes, getOptions, tieneExpProyectoValido, tieneExpRendicionValido, datosGenerales.area_responsable]);

    // Effects
    useEffect(() => {
        if (estado === ESTADO_CONFECCION_EXPEDIENTE) {
            setButtonIsDisabledState(true);
        } else {
            setButtonIsDisabledState(false);
        }
    }, [estado]);

    // Cargar datos cuando cambia formA.array_obras_expediente
    useEffect(() => {
        if (!Array.isArray(formA?.array_obras_expediente)) {
            setExpedientes([{
                expediente_o: "",
                objeto_o: "",
                fecha_caratulacion_o: null,
                descripcion_objeto_o: ""
            }]);
            return;
        }
        
        const currentExpedientes = expedientesRef.current;
        const localHasRealData = currentExpedientes.some(exp => exp && exp.expediente_o && exp.expediente_o.trim() !== '');
        const localIsEmpty = !localHasRealData;
        
        if (formA.array_obras_expediente.length === 0) {
            setExpedientes([{
                expediente_o: "",
                objeto_o: "",
                fecha_caratulacion_o: null,
                descripcion_objeto_o: ""
            }]);
            return;
        }
        
        const isResetState = formA.array_obras_expediente.length === 1 && 
                             !formA.array_obras_expediente[0]?.expediente_o &&
                             !formA.array_obras_expediente[0]?.objeto_o;
        
        if (isResetState && localHasRealData) {
            montoAdendaRawRef.current = {};
            setExpedientes([{
                expediente_o: "",
                objeto_o: "",
                fecha_caratulacion_o: null,
                descripcion_objeto_o: ""
            }]);
            return;
        }
        
        const formAHasData = formA.array_obras_expediente.some(exp => exp && exp.expediente_o);
        const lengthsDiffer = formA.array_obras_expediente.length !== currentExpedientes.length;

        // Cambio de obra: hay que recargar aunque la cantidad de expedientes sea la
        // misma. Sin esto, entrar de una obra a otra con N=3 deja los 3 expedientes de
        // la anterior (todo lo demas de la obra si carga bien: solo este form queda
        // pegado). No se pisa lo que el usuario esta editando porque el id de obra no
        // cambia mientras edita: solo cambia cuando entra otra obra.
        const obraIdActual = datosGenerales?.id_partido_obra || null;
        const obraIdPrevio = obraIdDeExpedientesRef.current;
        const cambioDeObra = !!(obraIdActual && obraIdPrevio && obraIdActual !== obraIdPrevio);

        if (formAHasData && (localIsEmpty || lengthsDiffer || cambioDeObra)) {
            const procesados = formA.array_obras_expediente.map(exp => {
                if (exp && exp.expediente_o) {
                    const descompuesto = descomponerExpediente(exp.expediente_o);
                    if (descompuesto && descompuesto.esValido) {
                        if (descompuesto.esLegacy) {
                            return {
                                ...exp,
                                valorOriginal: exp.expediente_o,
                                formatoCorrecto: true,
                                esLegacy: true
                            };
                        }
                        const valorNormalizado = `EX-${descompuesto.exp_anio}-${descompuesto.exp_numero.padStart(8, '0')}-GDEBA-${descompuesto.exp_reparticion}`;
                        return {
                            ...exp,
                            expediente_o: valorNormalizado,
                            valorOriginal: exp.expediente_o,
                            exp_anio: descompuesto.exp_anio,
                            exp_numero: descompuesto.exp_numero,
                            exp_reparticion: descompuesto.exp_reparticion,
                            formatoCorrecto: true
                        };
                    } else {
                        return {
                            ...exp,
                            formatoCorrecto: false,
                            valorOriginal: exp.expediente_o,
                            valorOriginalInvalido: exp.expediente_o
                        };
                    }
                }
                return exp;
            });
            setExpedientes(procesados);
            montoAdendaRawRef.current = {};
            obraIdDeExpedientesRef.current = obraIdActual;
            // Si los datos vienen del contexto (edicion), marcar para saltar la validacion inicial
            if (formA.array_obras_expediente.some(exp => exp && exp.expediente_o)) {
                initialProcessedRef.current = true;
                // Snapshot de las claves de expedientes YA validados/guardados en el contexto,
                // para no volver a verificar existencia al editar (Proyecto y Rendición)
                const clavesGuardadas = new Set(
                    formA.array_obras_expediente
                        .filter(e => e && e.expediente_o)
                        .map(e => e.expediente_o && e.objeto_o ? `${e.expediente_o}|${e.objeto_o}` : e.expediente_o)
                );
                expedientesGuardadosRef.current = clavesGuardadas;
                // Pre-calcular el valor del expediente Proyecto para marcarlo como ya verificado
                const expProyecto = procesados.find(e => e?.objeto_o === 'Proyecto' && e?.expediente_o) || procesados[0];
                if (expProyecto?.expediente_o) {
                    ultimoValorVerificadoRef.current = `${expProyecto.expediente_o}|${expProyecto.objeto_o || 'Proyecto'}`;
                }
            }
        }
    }, [formA?.array_obras_expediente, descomponerExpediente, datosGenerales?.id_partido_obra]);

    // Sincronizar con el contexto
    useEffect(() => {
        if (!Array.isArray(expedientes) || isUpdatingFromEffect.current) return;

        const contextData = formA?.array_obras_expediente;
        const contextHasData = Array.isArray(contextData) && contextData.some(e => e && e.expediente_o);
        const localHasData = expedientes.some(e => e && e.expediente_o);

        // Si context tiene datos pero local está vacío → data loading en curso, no sobreescribir.
        // EXCEPCIÓN: si el usuario editó (desmarcó el checkbox / limpió a propósito), sí sincronizar
        if (contextHasData && !localHasData && !hasPendingEditRef.current) return;

        isUpdatingFromEffect.current = true;
        // Solo sincronizar al contexto si el usuario realmente editó.
        // Si es carga inicial, mantener los datos originales (sin transformación)
        // para evitar que valorOriginal, exp_numero formateado, etc. se persistan.
        if (hasPendingEditRef.current) {
            loadField('formA', 'array_obras_expediente', null, expedientes);
        }
        isEditingLocally.current = false;
        setTimeout(() => {
            isUpdatingFromEffect.current = false;
        }, 0);
    }, [expedientes, loadField]);

    // Verificar si el expediente existe en el backend
    useEffect(() => {
        // Si estamos editando y un expediente local (Proyecto, Rendición, Adenda,
        // etc.) no cambió respecto a los datos originales del contexto, no
        // re-validar existencia (ya fue validado durante la creación original).
        // Mismo comportamiento que el fix de Proyecto, generalizado a todos.
        const contextData = formA?.array_obras_expediente;
        const contextHasSavedData = Array.isArray(contextData) && contextData.some(e => e && e.expediente_o);
        if (contextHasSavedData) {
            const clavesContexto = new Set(
                contextData
                    .filter(e => e && e.expediente_o)
                    .map(e => (e.expediente_o && e.objeto_o) ? `${e.expediente_o}|${e.objeto_o}` : e.expediente_o)
            );
            expedientes.forEach(exp => {
                if (exp && exp.expediente_o && exp.objeto_o) {
                    const clave = `${exp.expediente_o}|${exp.objeto_o}`;
                    if (clavesContexto.has(clave)) {
                        // El expediente no cambió: marcarlo como ya verificado para
                        // que se saltee en la verificación contra la BD, sin importar
                        // si el snapshot del contexto se cargó o no en este ciclo.
                        expedientesGuardadosRef.current.add(clave);
                    }
                }
            });
        }

        // Si los datos se cargaron del contexto (editar registro existente),
        // saltar la validacion para no mostrar falsamente "ya existe en la BD"
        if (initialProcessedRef.current) {
            return;
        }
        
        // En remount (cambio de pestaña), si el contexto tiene expedientes guardados
        // y no hay ediciones pendientes, es recarga de datos → no verificar
        if (!initialProcessedRef.current && !hasPendingEditRef.current) {
            const contextData = formA?.array_obras_expediente;
            const contextHasSavedData = Array.isArray(contextData) &&
                contextData.some(e => e && e.expediente_o);
            const localHasData = expedientes.some(e => e && e.expediente_o);
            if (contextHasSavedData && localHasData) {
                return;
            }
        }

        const verificarExpedientesEnBD = async () => {
            if (!expedientes || expedientes.length === 0) return;

            // Verificar cada expediente contra su propio objeto
            const resultados = {};
            const verificadosAmbos = [...expedientes.entries()];

            await Promise.all(verificadosAmbos.map(async ([index, expediente]) => {
                if (!expediente) return;

                // No verificar expedientes sin objeto asignado
                if (!expediente.objeto_o) {
                    resultados[index] = { existe: null, info: null, esMismaObra: false, fallida: false };
                    return;
                }

                // Calcular el valor actual basado en los campos desglosados
                const anioActual = expediente?.exp_anio || '';
                const numeroActual = expediente?.exp_numero || '';
                const reparticionActual = expediente?.exp_reparticion || '';

                const tieneAnioValido = anioActual.length === 4;
                const tieneNumeroValido = numeroActual.length > 0;
                const tieneReparticionValida = reparticionActual.length > 0;

                let valorExpediente = '';
                let tieneFormatoValido = false;

                if (expediente?.esLegacy) {
                    valorExpediente = expediente.expediente_o || '';
                    tieneFormatoValido = esFormatoValido(valorExpediente);
                } else if (tieneAnioValido && tieneNumeroValido && tieneReparticionValida) {
                    valorExpediente = `EX-${anioActual}-${numeroActual.padStart(8, '0')}-GDEBA-${reparticionActual}`;
                    tieneFormatoValido = esFormatoValido(valorExpediente);
                } else if (expediente?.expediente_o && esFormatoValido(expediente.expediente_o)) {
                    valorExpediente = expediente.expediente_o;
                    tieneFormatoValido = true;
                }

                // Verificar solo si hay valor y formato válido
                if (valorExpediente && tieneFormatoValido) {
                    const claveVerificacion = `${valorExpediente}|${expediente.objeto_o}`;
                    // Si el expediente ya estaba guardado en el contexto (se cargó desde BD al editar),
                    // ya fue validado en su creación original → no re-verificar existencia.
                    if (expedientesGuardadosRef.current?.has(claveVerificacion)) {
                        return;
                    }
                    // Evitar verificar el mismo valor múltiples veces
                    if (ultimoValorVerificadoRef.current === claveVerificacion) {
                        return;
                    }

                    ultimoValorVerificadoRef.current = claveVerificacion;

                    try {
                        const objetoParam = encodeURIComponent(expediente.objeto_o);
                        const response = await fetch(`/api/verificar-expediente/${encodeURIComponent(valorExpediente)}?objeto=${objetoParam}`, {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            }
                        });

                        const data = await response.json();
                        if (data.success) {
                            const esMismaObra = data.existe && data.data?.cg_01_id_partido_obra === datosGenerales.id_partido_obra;
                            resultados[index] = {
                                existe: esMismaObra ? null : data.existe,
                                info: data.existe ? data.data : null,
                                esMismaObra,
                                fallida: false
                            };
                        } else {
                            resultados[index] = { existe: null, info: null, esMismaObra: false, fallida: true };
                        }
                    } catch (error) {
                        console.error('Error al verificar expediente en BD:', error);
                        resultados[index] = { existe: null, info: null, esMismaObra: false, fallida: true };
                    }
                } else {
                    // Resetear si no cumple las condiciones (formato inválido o sin valor)
                    resultados[index] = { existe: null, info: null, esMismaObra: false, fallida: false };
                }
            }));

            setVerificacion(resultados);
        };

        verificarExpedientesEnBD();
        hasPendingEditRef.current = false;
    }, [expedientes, esFormatoValido, datosGenerales.id_partido_obra, token]);

    // Cuando el Proyecto ya existe en BD, deshabilitar boton Añadir Expediente y marcar flag en contexto
    useEffect(() => {
        const idxProyecto = expedientes.findIndex(e => e && e.objeto_o === 'Proyecto');
        const resultadoProyecto = idxProyecto !== -1 ? (verificacion[idxProyecto] || {}) : {};
        if (resultadoProyecto.existe === true) {
            setButtonIsDisabledState(true);
            loadField('formA', 'datosGenerales', 'expediente_duplicado', true);
        } else {
            const incompleto = expedientes.some(exp => {
                if (exp?.objeto_o !== 'Proyecto' && exp?.objeto_o !== 'Rendición') return false;
                if (exp?.esLegacy) return !/^2423-\d{4}\/\d{2}$/.test(exp.expediente_o || '');
                const anioValido = exp?.exp_anio && exp.exp_anio.length === 4;
                const numeroValido = exp?.exp_numero && exp.exp_numero.length > 0;
                const reparticionValida = exp?.exp_reparticion && exp.exp_reparticion.length > 0;
                return !(anioValido && numeroValido && reparticionValida);
            });
            const disabledByEstado = estado === ESTADO_CONFECCION_EXPEDIENTE;
            setButtonIsDisabledState(disabledByEstado || incompleto);
            loadField('formA', 'datosGenerales', 'expediente_duplicado', false);
        }
    }, [verificacion, estado, loadField, expedientes]);

    // Si se selecciono Proyecto pero el expediente esta incompleto, bloquear guardado
    useEffect(() => {
        const tieneExpedienteIncompleto = expedientes.some(exp => {
            if (!exp?.objeto_o) return false;
            // Formato legacy 2423-XXXX/YY: validar que el valor real cumpla el patron
            if (exp?.esLegacy) return !/^2423-\d{4}\/\d{2}$/.test(exp.expediente_o || '');
            const anioValido = exp?.exp_anio && exp.exp_anio.length === 4;
            const numeroValido = exp?.exp_numero && exp.exp_numero.length > 0;
            const reparticionValida = exp?.exp_reparticion && exp.exp_reparticion.length > 0;
            // Valor libre sin desglose (ej. legacy o texto no descompuesto): validar formato completo
            if (!exp?.exp_anio && !exp?.exp_numero && !exp?.exp_reparticion) {
                return !(exp?.expediente_o && esFormatoValido(exp.expediente_o));
            }
            return !(anioValido && numeroValido && reparticionValida);
        });
        loadField('formA', 'datosGenerales', 'expediente_incompleto', tieneExpedienteIncompleto);
    }, [expedientes, loadField, esFormatoValido]);

    // ─── COMENTADO TEMPORALMENTE ───
    // Estado automático según expedientes: Proyecto + Rendición válidos → Seguimiento.
    // Solo Proyecto válido → Circuito administrativo. Sin Proyecto válido → Análisis preliminar.
    // useEffect(() => {
    //     // Si context guardado tiene Rendición pero local no → data loading en curso
    //     // Solo bloquear si local está completamente vacío (sin datos aún),
    //     // si local ya tiene expedientes con datos es una acción del usuario (eliminó Rendición)
    //     const contextData = formA?.array_obras_expediente;
    //     const contextHasRendicion = Array.isArray(contextData) &&
    //         contextData.some(e => e.objeto_o === 'Rendición' && e.expediente_o);
    //     if (contextHasRendicion && !tieneExpRendicionValido) {
    //         const localTieneDatos = expedientes.some(e => e && e.expediente_o);
    //         if (!localTieneDatos) return;
    //     }
    //
    //     if (tieneExpProyectoValido && tieneExpRendicionValido && expedienteExiste !== true) {
    //         if (estado === 'Análisis preliminar' || estado === 'En circuito administrativo') {
    //             loadField('formA', 'datosGenerales', 'estado', 'Seguimiento');
    //         }
    //     } else if (tieneExpProyectoValido && expedienteExiste !== true) {
    //         if (estado === 'Análisis preliminar' || estado === 'Seguimiento') {
    //             loadField('formA', 'datosGenerales', 'estado', 'En circuito administrativo');
    //         }
    //     } else {
    //         if (estado === 'En circuito administrativo' || estado === 'Seguimiento') {
    //             loadField('formA', 'datosGenerales', 'estado', 'Análisis preliminar');
    //         }
    //     }
    // }, [tieneExpProyectoValido, tieneExpRendicionValido, expedienteExiste, estado, loadField]);

    // Manejadores
    const handleAddExpediente = useCallback(() => {
        initialProcessedRef.current = false;
        hasPendingEditRef.current = true;
        markSectionDirty('formA');
        // La lista crece: los indices dejan de corresponder al buffer de tipeo.
        montoAdendaRawRef.current = {};
        const newExpediente = {
            expediente_o: "",
            objeto_o: "",
            fecha_caratulacion_o: null,
            descripcion_objeto_o: ""
        };
        setExpedientes(prev => [...prev, newExpediente]);
    }, [markSectionDirty]);

    const handleRemoveExpediente = useCallback((index) => {
        initialProcessedRef.current = false;
        hasPendingEditRef.current = true;
        markSectionDirty('formA');
        if (expedientes.length > 1) {
            // Al quitar una fila se corren los indices del buffer de tipeo.
            montoAdendaRawRef.current = {};
            setExpedientes(prev => prev.filter((_, i) => i !== index));
        }
    }, [expedientes.length, markSectionDirty]);

    const handleClearExpediente = useCallback((index) => {
        markSectionDirty('formA');
        setExpedientes(prev => {
            const newExpedientes = [...prev];
            newExpedientes[index] = {
                ...newExpedientes[index],
                expediente_o: '',
                valorOriginal: '',
                exp_anio: '',
                exp_numero: '',
                exp_reparticion: '',
                es_legacy: false,
                esLegacy: false,
                formatoCorrecto: undefined,
                valorOriginalInvalido: undefined,
                valorOriginalPegado: undefined
            };
            return newExpedientes;
        });
    }, [markSectionDirty]);

    const aplicarPegadoDesglosado = useCallback((index, texto) => {
        initialProcessedRef.current = false;
        hasPendingEditRef.current = true;
        markSectionDirty('formA');
        const descompuesto = descomponerExpediente(texto);
        setExpedientes(prev => {
            const newExpedientes = [...prev];
            const current = newExpedientes[index] || {};
            if (descompuesto && descompuesto.esValido) {
                if (descompuesto.esLegacy) {
                    newExpedientes[index] = {
                        ...current,
                        expediente_o: texto,
                        valorOriginal: texto,
                        formatoCorrecto: true,
                        es_legacy: true,
                        esLegacy: true
                    };
                } else {
                    newExpedientes[index] = {
                        ...current,
                        expediente_o: `EX-${descompuesto.exp_anio}-${descompuesto.exp_numero.padStart(8, '0')}-GDEBA-${descompuesto.exp_reparticion}`,
                        valorOriginal: texto,
                        exp_anio: descompuesto.exp_anio,
                        exp_numero: descompuesto.exp_numero.padStart(8, '0'),
                        exp_reparticion: descompuesto.exp_reparticion,
                        valorOriginalPegado: texto,
                        formatoCorrecto: true,
                        es_legacy: false,
                        esLegacy: false
                    };
                }
            } else {
                newExpedientes[index] = {
                    ...current,
                    exp_anio: texto.replace(/\D/g, '').slice(0, 4) || '',
                    exp_numero: texto.replace(/\D/g, '').slice(0, 8) || '',
                    formatoCorrecto: undefined
                };
            }
            return newExpedientes;
        });
    }, [descomponerExpediente, markSectionDirty]);

    const handlePasteDesglosado = useCallback(async (index, event) => {
        event.preventDefault();
        const text = event.clipboardData ? event.clipboardData.getData('text') : '';
        const trimmed = (text || '').trim();
        if (trimmed) {
            aplicarPegadoDesglosado(index, trimmed);
        }
    }, [aplicarPegadoDesglosado]);

    // Pegar desde portapapeles mediante botón (para usuarios que no conocen el atajo)
    const handlePegarDesdeClipboard = useCallback(async (index) => {
        try {
            if (!navigator.clipboard || typeof navigator.clipboard.readText !== 'function') {
                return;
            }
            const text = await navigator.clipboard.readText();
            const trimmed = (text || '').trim();
            if (trimmed) {
                aplicarPegadoDesglosado(index, trimmed);
            }
        } catch (error) {
            console.error('Error al leer portapapeles:', error);
        }
    }, [aplicarPegadoDesglosado]);

    // Mostrar el botón "Pegar" solo si hay datos en el portapapeles
    const [clipboardHasData, setClipboardHasData] = useState(false);

    useEffect(() => {
        const leerClipboard = async () => {
            try {
                if (!navigator.clipboard || typeof navigator.clipboard.readText !== 'function') {
                    setClipboardHasData(false);
                    return;
                }
                const text = await navigator.clipboard.readText();
                const trimmed = (text || '').trim();
                setClipboardHasData(!!trimmed);
            } catch (error) {
                setClipboardHasData(false);
            }
        };

        // Chequear al montar (pequeña demora para que el navegador considere el gesto del usuario)
        const timer = setTimeout(() => { leerClipboard(); }, 500);
        // Re-chequear cuando el usuario vuelve a la pestaña (puede haber copiado algo en otro lado)
        window.addEventListener('focus', (leerClipboard));

        return () => {
            clearTimeout(timer);
            window.removeEventListener('focus', leerClipboard);
        };
    }, []);

    const handleExpedienteChange = useCallback((index, field, value) => {
        initialProcessedRef.current = false;
        hasPendingEditRef.current = true;
        isEditingLocally.current = true;
        markSectionDirty('formA');

        // Si la fila deja de ser Adenda (o pasa a serlo), el buffer de tipeo del
        // monto ya no aplica: el valor vigente es el guardado en el expediente.
        if (field === 'objeto_o') {
            delete montoAdendaRawRef.current[index];
        }
        
        setExpedientes(prev => {
            const newExpedientes = [...prev];
            const currentExp = newExpedientes[index] || {};
            
            // Toggle formato legacy (2423-XXXXX/XX): al tildar se carga sin desglose, al destildar desglosado
            if (field === 'es_legacy') {
                const esLegacy = !!value;
                if (esLegacy) {
                    // Cambiar a modo sin desglose: limpiar campos desglosados y texto previo
                    newExpedientes[index] = {
                        ...currentExp,
                        es_legacy: true,
                        esLegacy: true,
                        expediente_o: '',
                        valorOriginal: '',
                        exp_anio: '',
                        exp_numero: '',
                        exp_reparticion: '',
                        formatoCorrecto: undefined,
                        valorOriginalInvalido: undefined
                    };
                } else {
                    // Cambiar a modo desglosado: limpiar texto sin desglose y flags legacy
                    newExpedientes[index] = {
                        ...currentExp,
                        es_legacy: false,
                        esLegacy: false,
                        expediente_o: '',
                        valorOriginal: '',
                        formatoCorrecto: undefined,
                        valorOriginalInvalido: undefined
                    };
                }
                return newExpedientes;
            }
            
            if (field === 'expediente_o' && value) {
                const esLegacyMode = currentExp.es_legacy || currentExp.esLegacy;
                // Si está activo el modo legacy (checkbox 2423), SOLO aceptar formato 2423-XXXX/XX
                if (esLegacyMode) {
                    const es2423Valido = /^2423-\d{4}\/\d{2}$/.test(value.trim());
                    newExpedientes[index] = {
                        ...currentExp,
                        expediente_o: value,
                        valorOriginal: value,
                        es_legacy: true,
                        esLegacy: true,
                        formatoCorrecto: es2423Valido,
                        valorOriginalInvalido: es2423Valido ? undefined : value,
                        exp_anio: '',
                        exp_numero: '',
                        exp_reparticion: ''
                    };
                    return newExpedientes;
                }
                // Si se escribe un patrón 2423 sin el checkbox, activar legacy automáticamente
                if (/^2423-/.test(value)) {
                    const descompuesto = descomponerExpediente(value);
                    if (descompuesto?.esLegacy) {
                        newExpedientes[index] = {
                            ...currentExp,
                            expediente_o: value,
                            valorOriginal: value,
                            es_legacy: true,
                            esLegacy: true,
                            formatoCorrecto: true,
                            exp_anio: '',
                            exp_numero: '',
                            exp_reparticion: ''
                        };
                        return newExpedientes;
                    }
                    // 2423 incompleto/inválido: dejar editar, formato aún inválido
                    newExpedientes[index] = {
                        ...currentExp,
                        expediente_o: value,
                        valorOriginal: value,
                        es_legacy: true,
                        esLegacy: true,
                        formatoCorrecto: false,
                        valorOriginalInvalido: value,
                        exp_anio: '',
                        exp_numero: '',
                        exp_reparticion: ''
                    };
                    return newExpedientes;
                }
                value = aplicarMascaraExpediente(value);
            }
            
            if (field === 'exp_anio') {
                value = value.replace(/\D/g, '').slice(0, 4);
            } else if (field === 'exp_numero') {
                value = value.replace(/\D/g, '').slice(0, 8);
            }
            
            const updatedExp = {
                ...currentExp,
                [field]: value
            };
            
            // Si cambió un campo descompuesto, reconstruir expediente_o
            if (['exp_anio', 'exp_numero', 'exp_reparticion'].includes(field)) {
                const anio = field === 'exp_anio' ? value : (updatedExp.exp_anio || '');
                const numero = field === 'exp_numero' ? value : (updatedExp.exp_numero || '');
                const reparticion = field === 'exp_reparticion' ? value : (updatedExp.exp_reparticion || '');
                
                const tieneAnioValido = anio && anio.length === 4;
                const tieneNumeroValido = numero && numero.length > 0;
                const tieneReparticionValida = reparticion && reparticion.length > 0;
                
                if (tieneAnioValido && tieneNumeroValido && tieneReparticionValida) {
                    const numeroFormateado = numero.padStart(8, '0');
                    // Completar con ceros al elegir repartición/anio, pero no al editar el número (mantener editable)
                    if (field !== 'exp_numero') {
                        updatedExp.exp_numero = numeroFormateado;
                    }
                    updatedExp.expediente_o = `EX-${anio}-${numeroFormateado}-GDEBA-${reparticion}`;
                    updatedExp.formatoCorrecto = true;
                } else {
                    updatedExp.expediente_o = '';
                    updatedExp.formatoCorrecto = false;
                }
            }
            
            newExpedientes[index] = updatedExp;
            return newExpedientes;
        });
    }, [aplicarMascaraExpediente, descomponerExpediente, markSectionDirty]);

    // Render condicional
    if (!datosGenerales.area_responsable || !estado) {
        return (
            <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <p className="text-sm text-red-600 dark:text-red-400">
                    {(() => {
                        if (!datosGenerales.area_responsable && !estado) {
                            return <><span className="font-medium">Atención:</span> Por favor, seleccione un Área Responsable y un Estado para continuar.</>;
                        }
                        if (!datosGenerales.area_responsable) {
                            return <><span className="font-medium">Atención:</span> Por favor, seleccione un Área Responsable para continuar.</>;
                        }
                        if (!estado) {
                            return <><span className="font-medium">Atención:</span> Por favor, seleccione un Estado para continuar.</>;
                        }
                        return null;
                    })()}
                </p>
            </div>
        );
    }
 
    const overlayDisabled = estado === ESTADO_CONFECCION_EXPEDIENTE;

    return (
        <fieldset disabled={overlayDisabled}>
            <div className="relative">
                {overlayDisabled && (
                    <div className="absolute inset-0 bg-gray-200/30 dark:bg-gray-900/30 z-10 rounded-lg pointer-events-none" />
                )}
            <div className={`p-4 ${overlayDisabled ? 'opacity-75' : ''}`}>
                {expedientes?.map((expediente = {}, index) => (
                    <div key={index} className="mb-4 border rounded-lg p-4 bg-white dark:bg-gray-800 shadow-sm">
                        {expedientes.length > 0 && (
                            <div className="flex justify-end mt-2">
                                <Button 
                                    variant={expedientes.length <= 1 ? "ghost" : "destructive"}
                                    size="sm"
                                    onClick={() => handleRemoveExpediente(index)}
                                    className={`px-2 py-1 text-xs ${expedientes.length <= 1 ? "text-gray-400 cursor-not-allowed" : ""}`}
                                    disabled={expedientes.length <= 1}
                                >
                                    Eliminar
                                </Button>
                            </div>
                        )}
                        <div className="mb-4">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="objetos">Objeto</Label>
                                <label className="inline-flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={!!expediente?.es_legacy}
                                        onChange={(e) => handleExpedienteChange(index, 'es_legacy', e.target.checked)}
                                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    Formato 2423-XXXX/XX (sin desglose)
                                </label>
                            </div>
                            <div className="mt-2">
                                    <Select_simple
                                        options={getOptionsConLimites(index)}
                                        id={`objeto_o_${index}`}
                                        name="objeto_o"
                                        value={expediente?.objeto_o || ''}
                                        onChange={(e) => handleExpedienteChange(index, 'objeto_o', e.target.value)}
                                    />
                            </div>
                        </div>
                        {expediente?.objeto_o && (
                        <>
                        {/* Leyenda de formato del expediente */}
                        {(() => {
                            const entrada = expediente?.valorOriginal || expediente?.expediente_o;
                            if (!entrada) return null;
                            
                            const esLegacyModo = expediente?.es_legacy || expediente?.esLegacy;
                            // En modo legacy (checkbox 2423) solo vale el patrón estricto 2423-XXXX/XX
                            const esValido = esLegacyModo
                                ? /^2423-\d{4}\/\d{2}$/.test(entrada.trim())
                                : esFormatoValido(entrada);
                            const normalizado = normalizarExpediente(entrada);
                            const formatoEsperado = esLegacyModo
                                ? "(Formato: 2423-XXXX/YY)"
                                : "(Formato: EX-YYYY-XXXXXXXX-GDEBA-XXXXXXXX o 2423-XXXX/YY)";
                            
                            // Verificar formato actual basado en campos desglosados
                            const anioActual = expediente?.exp_anio || '';
                            const numeroActual = expediente?.exp_numero || '';
                            const reparticionActual = expediente?.exp_reparticion || '';
                            const tieneDatosCompletos = anioActual.length === 4 && numeroActual.length > 0 && reparticionActual.length > 0;
                            const valorBDActual = tieneDatosCompletos 
                                ? `EX-${anioActual}-${numeroActual.padStart(8, '0')}-GDEBA-${reparticionActual}`
                                : '';
                            const formatoBDValido = valorBDActual && esFormatoValido(valorBDActual);
                            const formatoLegacyValido = expediente?.esLegacy && expediente?.expediente_o && esFormatoValido(expediente.expediente_o);
                            const formatoDirectoValido = !expediente?.esLegacy && expediente?.expediente_o && esFormatoValido(expediente.expediente_o);
                            const formatoValidoParaFeedback = formatoBDValido || formatoLegacyValido || formatoDirectoValido;
                            const verifIndex = verificacion[index] || { existe: null, info: null, esMismaObra: false, fallida: false };
                            
                            // Si es valido pero no hay feedback de existencia, ocultar toda la leyenda
                            if (esValido) {
                                const hayFeedback = formatoValidoParaFeedback && (verifIndex.existe !== null || verifIndex.esMismaObra || verifIndex.fallida);
                                if (!hayFeedback) return null;
                            }

                            return (
                            <div className="mt-4 p-3 border border-gray-200 rounded-lg bg-gray-50 dark:bg-gray-900/20 w-full">
                                    {esValido ? (
                                        <div className="space-y-2">
                                            {formatoValidoParaFeedback && (verifIndex.existe !== null || verifIndex.esMismaObra || verifIndex.fallida) && (
                                                <div className={`p-2 rounded text-sm ${
                                                    verifIndex.fallida
                                                        ? 'bg-red-100 dark:bg-red-900/30 border border-red-300'
                                                        : verifIndex.existe
                                                            ? 'bg-amber-100 dark:bg-amber-900/30 border border-amber-300'
                                                            : 'bg-green-100 dark:bg-green-900/30 border border-green-300'
                                                }`}>
                                                    {verifIndex.fallida ? (
                                                        <div>
                                                            <span className="font-semibold text-red-700 dark:text-red-300">
                                                                ⚠ No se pudo verificar el expediente en la base de datos
                                                            </span>
                                                            <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                                                                Verifique que el servidor esté disponible e intente nuevamente
                                                            </div>
                                                        </div>
                                                    ) : verifIndex.existe ? (
                                                        <div>
                                                            <span className="font-semibold text-amber-700 dark:text-amber-300">
                                                                ⚠ El expediente ya existe en la base de datos
                                                            </span>
                                                            {verifIndex.info && (
                                                                <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                                                                    Obra: {verifIndex.info.nombre_obra} (ID: {verifIndex.info.cg_01_id_partido_obra})
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : verifIndex.esMismaObra ? (
                                                        <span className="font-semibold text-blue-700 dark:text-blue-300">
                                                            ✓ El expediente corresponde a la obra actual
                                                        </span>
                                                    ) : (
                                                        <span className="font-semibold text-green-700 dark:text-green-300">
                                                            ✓ El expediente no existe en la base de datos
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : !esLegacyModo && normalizado ? (
                                        <div className="w-full">
                                            <div className="text-sm mt-2 p-2 bg-white rounded border border-amber-200">
                                                <span className="text-gray-600">Original: </span>
                                                <span className="line-through text-gray-500">{entrada}</span>
                                                <span className="text-green-600 ml-2 font-medium">Normalizado → {normalizado}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="w-full">
                                            <div className="flex items-center gap-2">
                                                <span className="text-red-600 font-medium">
                                                    ✗ Formato incorrecto
                                                </span>
                                                <span className="text-gray-500 text-xs">
                                                    {formatoEsperado}
                                                </span>
                                            </div>
                                            <div className="text-sm mt-2 p-2 bg-white rounded border border-red-200">
                                                <span className="text-gray-600">Valor: </span>
                                                <span className="font-mono bg-gray-100 px-1 rounded">{entrada}</span>
                                            </div>
                                        </div>
                                    )}
                            </div>
                            );
                        })()}
                        
                        {(() => {
                            const esLegacyActivo = expediente?.es_legacy || expediente?.esLegacy;
                            const esNoDesgloseable = esLegacyActivo ||
                                (expediente?.expediente_o && !(expediente?.exp_anio || expediente?.exp_numero || expediente?.exp_reparticion));
                            if (esNoDesgloseable) {
                                return (
                                <div className="mt-4 p-3 border border-amber-200 rounded-lg bg-amber-50 dark:bg-amber-900/20 w-full">
                                    <div className="flex items-center justify-between mb-2">
                                        <Label htmlFor={`expediente_o_${index}`} className="text-amber-700 dark:text-amber-300 text-sm font-semibold block">
                                            Expediente (sin desglose)
                                        </Label>
                                        <div className="flex items-center gap-1">
{clipboardHasData && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handlePegarDesdeClipboard(index)}
                                                    title="Pegar desde portapapeles"
                                                    className="px-2 py-1 h-7 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
                                                    disabled={disabled}
                                                >
                                                    📋 Pegar
                                                </Button>
                                            )}
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleClearExpediente(index)}
                                                title="Limpiar y restablecer al estado inicial"
                                                className="px-2 py-1 h-7 text-red-500 hover:bg-red-50 hover:text-red-600"
                                            >
                                                ✕ Limpiar
                                            </Button>
                                        </div>
                                    </div>
                                    <MemoizedInput
                                        id={`expediente_o_${index}`}
                                        name="expediente_o"
                                        type="text"
                                        placeholder={(expediente?.es_legacy || expediente?.esLegacy) ? "2423-XXXX/YY" : "EX-2025-00000001-GDEBA-DPPHMDCGP o 2423-XXXXX/XX"}
                                        value={expediente?.expediente_o || ''}
                                        onChange={(e) => handleExpedienteChange(index, 'expediente_o', e.target.value)}
                                    />
                                </div>
                                );
                            }
                            return null;
                        })()}
                        {/* Inputs desglosados (oculto para formato legacy 2423-XXXX/YY, invalido o texto libre) */}
                        {expediente?.es_legacy || expediente?.esLegacy || (expediente?.expediente_o && !(expediente?.exp_anio || expediente?.exp_numero || expediente?.exp_reparticion)) ? null : (
                        <div className="mt-4 p-3 border border-blue-200 rounded-lg bg-blue-50 dark:bg-blue-900/20 w-full">
                            <div className="flex items-center justify-between mb-2">
                                <Label className="text-blue-700 dark:text-blue-300 text-sm font-semibold block">
                                    Expediente desglosado
                                </Label>
                                <div className="flex items-center gap-1">
{clipboardHasData && (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handlePegarDesdeClipboard(index)}
                                                        title="Pegar desde portapapeles"
                                                        className="px-2 py-1 h-7 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
                                                        disabled={disabled}
                                                    >
                                                        📋 Pegar
                                                    </Button>
                                                )}
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleClearExpediente(index)}
                                        title="Limpiar y restablecer al estado inicial"
                                        className="px-2 py-1 h-7 text-red-500 hover:bg-red-50 hover:text-red-600"
                                    >
                                        ✕ Limpiar
                                    </Button>
                                </div>
                            </div>
                            <div className="flex flex-nowrap items-center gap-2 w-full">
                                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                    EX-
                                </div>
                                
                                <div className="flex-1 min-w-[70px] max-w-[90px]">
                                    <input
                                        id={`exp_anio_${index}`}
                                        type="text"
                                        placeholder="2025"
                                        value={expediente?.exp_anio || ''}
                                        onPaste={(e) => handlePasteDesglosado(index, e)}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                                            handleExpedienteChange(index, 'exp_anio', val);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                
                                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                    -
                                </div>
                                
                                <div className="flex-1 min-w-[100px] max-w-[140px]">
                                    <input
                                        id={`exp_numero_${index}`}
                                        type="text"
                                        placeholder="02314791"
                                        value={expediente?.exp_numero || ''}
                                        onPaste={(e) => handlePasteDesglosado(index, e)}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, 8);
                                            handleExpedienteChange(index, 'exp_numero', val);
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                
                                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                    -GDEBA-
                                </div>
                                
                                <div className="flex-[2] min-w-[140px]">
                                    <Select_simple
                                        id={`exp_reparticion_${index}`}
                                        options={optionsReparticion}
                                        value={expediente?.exp_reparticion || ''}
                                        onChange={(e) => handleExpedienteChange(index, 'exp_reparticion', e.target.value)}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                            
                            {/* Mostrar el valor a enviar a BD */}
                            {(expediente?.exp_anio || expediente?.exp_numero || expediente?.exp_reparticion) && (
                                <div className="mt-3 space-y-2">
                                    {(() => {
                                        const anioActual = expediente?.exp_anio || '';
                                        const numeroActual = expediente?.exp_numero || '';
                                        const reparticionActual = expediente?.exp_reparticion || '';
                                        
                                        const tieneAnioValido = anioActual.length === 4;
                                        const tieneNumeroValido = numeroActual.length > 0;
                                        const tieneReparticionValida = reparticionActual.length > 0;
                                        
                                        let valorBD = '';
                                        let esValido = false;
                                        
                                        if (tieneAnioValido && tieneNumeroValido && tieneReparticionValida) {
                                            valorBD = `EX-${anioActual}-${numeroActual.padStart(8, '0')}-GDEBA-${reparticionActual}`;
                                            esValido = esFormatoValido(valorBD);
                                        }
                                        
                                        return (
                                            <div className={`p-2 rounded text-sm ${esValido ? 'bg-green-100 dark:bg-green-900/30 border border-green-300' : 'bg-red-100 dark:bg-red-900/30 border border-red-300'}`}>
                                                <span className="font-semibold">Valor a enviar a BD:</span>
                                                <span className="ml-2 font-mono">{valorBD || '(incompleto)'}</span>
                                                {valorBD && (
                                                    esValido ? (
                                                        <span className="ml-2 text-green-700 dark:text-green-300 font-medium">✓ Formato correcto</span>
                                                    ) : (
                                                        <span className="ml-2 text-red-700 dark:text-red-300 font-medium">✗ Formato incorrecto</span>
                                                    )
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                            
                            {expediente?.formatoCorrecto === false && expediente?.valorOriginalInvalido && (
                                <div className="mt-2 p-2 rounded text-sm bg-red-100 dark:bg-red-900/30 border border-red-300">
                                    <span className="text-red-700 dark:text-red-300 font-medium">
                                        ⚠ Formato incorrecto: El expediente "{expediente.valorOriginalInvalido}" no tiene un formato válido (EX-YYYY-XXXXXXXX-GDEBA-XXXXXXXX o 2423-XXXX/YY)
                                    </span>
                                </div>
                            )}
                        </div>
                        )}
                        
                        {expediente.objeto_o === 'Adenda' && (
                            <div className="grid grid-cols-2 gap-4 mt-4">
                                <div>
                                    <Label htmlFor="monto_adenda">Monto adenda</Label>
                                    <div className="mt-1">
                                        <MemoizedInput
                                            id={`monto_adenda_${index}`}
                                            name="monto_adenda"
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="$ Ej: 1.234,56"
                                            value={(() => {
                                                const raw = montoAdendaRawRef.current[index] !== undefined
                                                    ? montoAdendaRawRef.current[index]
                                                    : String(expediente?.monto_adenda || '');
                                                const formateado = formatMontoDisplay(raw);
                                                return formateado ? `$${formateado}` : '';
                                            })()}
                                            onChange={(e) => {
                                                const displayed = e.target.value;
                                                const cursorDisp = e.target.selectionStart;
                                                // Convertir cursor del display (con $, puntos) a raw (sin puntos)
                                                let rawCursor = 0;
                                                for (let i = 0; i < cursorDisp; i++) {
                                                    if (/[\d,]/.test(displayed[i])) rawCursor++;
                                                }
                                                const raw = cleanMontoInput(displayed);
                                                montoAdendaRawRef.current[index] = raw;
                                                handleExpedienteChange(index, 'monto_adenda', parseMonto(raw));
                                                requestAnimationFrame(() => {
                                                    const el = document.getElementById(`monto_adenda_${index}`);
                                                    if (el) {
                                                        // +1 por el símbolo $ que se agrega al mostrar
                                                        const newPos = computeCursorPos(raw, rawCursor) + (raw ? 1 : 0);
                                                        el.setSelectionRange(newPos, newPos);
                                                    }
                                                });
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4 mt-4">
                            <div>
                                <Label htmlFor="fecha_caratulacion">Fecha caratulación</Label>
                                <div className="mt-1 w-full">
                                    <DatePicker
                                        id={`fecha_caratulacion_o_${index}`}
                                        name="fecha_caratulacion_o"
                                        dateFormat="dd/MM/yyyy"
                                        selected={expediente?.fecha_caratulacion_o ? new Date(expediente.fecha_caratulacion_o) : null}
                                        className="w-full border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 h-10"
                                        onChange={(date) => handleExpedienteChange(index, 'fecha_caratulacion_o', date)}
                                        wrapperClassName="w-full"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="descripcion_objeto_o">Descripción</Label>
                                <div className="mt-1">
                                    <MemoizedInput
                                        id={`descripcion_objeto_o_${index}`}
                                        name="descripcion_objeto_o"
                                        type="text"
                                        placeholder="Descripción breve"
                                        value={expediente?.descripcion_objeto_o || ''}
                                        onChange={(e) => handleExpedienteChange(index, 'descripcion_objeto_o', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>                    
                        </>)}
                    </div>
                ))}

                {tieneExpProyecto && (datosGenerales.area_responsable !== AREA_RESP_MICROCREDITOS || !tieneExpRendicionValido) ? (
                    <div className="flex justify-end mt-4">
                        <Button
                            disabled={buttonIsDisabledState}
                            type="button"
                            onClick={handleAddExpediente}
                        >
                            Añadir Expediente
                        </Button>
                    </div>
                ) : null}

                {user === 'Javier' || user === 'Datos' && (
                    <div className="mt-4 p-2 bg-gray-100 rounded">
                        <pre>{JSON.stringify(expedientes, null, 2)}</pre>
                    </div>
                )}
            </div>
            </div>
            {overlayDisabled && (
                <span className="block text-sm text-red-600 dark:text-red-400 mt-2 ml-2">
                    *⚠️ No se pueden añadir expedientes en el estado {estado}.
                </span>
            )}
        </fieldset>
    );
};

export default memo(ExpedienteSubform);